/**
 * ============================================================================
 * CalibrationsTable — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: append-only calibration history. Outcome is colour + text so the
 *          chart scans without colour. A "correction of" pointer visualises
 *          the link between a FAILED / OUT_OF_TOLERANCE record and its
 *          subsequent corrective re-cal. Click a row → drawer with the
 *          measurements JSON (server-opaque payload).
 *
 * A11Y: the row is keyboard-actionable (button-in-cell). The drawer traps
 *  focus while open and closes on Escape.
 * ============================================================================ */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from './EquipmentDetail.module.css';
import type {
  CalibrationOutcome,
  CalibrationRecord,
} from './useEquipmentDetailQuery';

const OUTCOME_CLASS: Record<CalibrationOutcome, string> = {
  PASSED: styles.outcomePass,
  FAILED: styles.outcomeFail,
  OUT_OF_TOLERANCE: styles.outcomeOot,
};

export interface CalibrationsTableProps {
  rows: CalibrationRecord[];
}

export function CalibrationsTable({ rows }: CalibrationsTableProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );
  const [selected, setSelected] = useState<CalibrationRecord | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!selected) return;
    closeBtnRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setSelected(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  // Resolve `correction_of` from a real prior row so we can label the link
  // with the human-readable date instead of a bare id.
  const byId = useMemo(() => {
    const m = new Map<string, CalibrationRecord>();
    for (const r of rows) m.set(r.calibration_id, r);
    return m;
  }, [rows]);

  if (rows.length === 0) {
    return (
      <p className={styles.tabEmpty}>
        {t('equipment.detail.calibrations.empty', {
          defaultValue: 'No calibration records yet.',
        })}
      </p>
    );
  }

  return (
    <>
      <table
        className={styles.dataTable}
        aria-label={t('equipment.detail.calibrations.tableAria', {
          defaultValue: 'Calibration history',
        })}
      >
        <caption className={styles.srOnly}>
          {t('equipment.detail.calibrations.tableCaption', {
            defaultValue:
              'Append-only calibration history with outcome, source, technician, supervisor and a "correction of" link for re-tries.',
          })}
        </caption>
        <thead>
          <tr>
            <th scope="col">
              {t('equipment.detail.calibrations.columns.performedAt', {
                defaultValue: 'Performed at',
              })}
            </th>
            <th scope="col">
              {t('equipment.detail.calibrations.columns.outcome', {
                defaultValue: 'Outcome',
              })}
            </th>
            <th scope="col">
              {t('equipment.detail.calibrations.columns.source', {
                defaultValue: 'Source',
              })}
            </th>
            <th scope="col">
              {t('equipment.detail.calibrations.columns.technician', {
                defaultValue: 'Technician',
              })}
            </th>
            <th scope="col">
              {t('equipment.detail.calibrations.columns.supervisor', {
                defaultValue: 'Supervisor',
              })}
            </th>
            <th scope="col">
              {t('equipment.detail.calibrations.columns.measurements', {
                defaultValue: 'Measurements',
              })}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const corrects = r.correction_of_calibration_id
              ? byId.get(r.correction_of_calibration_id)
              : undefined;
            return (
              <tr key={r.calibration_id}>
                <td>
                  <time dateTime={r.performed_at_utc}>
                    {dateFmt.format(new Date(r.performed_at_utc))}
                  </time>
                  {corrects && (
                    <span
                      className={styles.correctionBadge}
                      aria-label={t(
                        'equipment.detail.calibrations.correctionAria',
                        {
                          defaultValue:
                            'Corrective re-cal of {{ts}}',
                          ts: dateFmt.format(new Date(corrects.performed_at_utc)),
                        },
                      )}
                    >
                      {t('equipment.detail.calibrations.correctionOf', {
                        defaultValue: '↩ corrects {{ts}}',
                        ts: dateFmt.format(new Date(corrects.performed_at_utc)),
                      })}
                    </span>
                  )}
                </td>
                <td>
                  <span className={OUTCOME_CLASS[r.outcome]}>
                    {t(`equipment.detail.calibrations.outcome.${r.outcome}`, {
                      defaultValue: r.outcome,
                    })}
                  </span>
                </td>
                <td>
                  {t(`equipment.detail.calibrations.source.${r.source}`, {
                    defaultValue: r.source,
                  })}
                </td>
                <td>{r.technician}</td>
                <td>
                  {r.supervisor ?? (
                    <span className={styles.subdued}>—</span>
                  )}
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="tertiary"
                    onClick={() => setSelected(r)}
                    aria-label={t(
                      'equipment.detail.calibrations.viewAria',
                      {
                        defaultValue:
                          'View measurements for calibration on {{ts}}',
                        ts: dateFmt.format(new Date(r.performed_at_utc)),
                      },
                    )}
                  >
                    {t('equipment.detail.calibrations.viewMeasurements', {
                      defaultValue: 'View',
                    })}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {selected && (
        <>
          <div
            className={styles.drawerBackdrop}
            aria-hidden="true"
            onClick={() => setSelected(null)}
          />
          <aside
            className={styles.drawer}
            role="dialog"
            aria-modal="true"
            aria-label={t('equipment.detail.calibrations.drawerAria', {
              defaultValue: 'Calibration measurements drawer',
            })}
          >
            <header className={styles.drawerHeader}>
              <h2 className={styles.drawerTitle}>
                {t('equipment.detail.calibrations.drawerTitle', {
                  defaultValue: 'Measurements — {{ts}}',
                  ts: dateFmt.format(new Date(selected.performed_at_utc)),
                })}
              </h2>
              <button
                ref={closeBtnRef}
                type="button"
                className={styles.drawerCloseBtn}
                onClick={() => setSelected(null)}
                aria-label={t('common.close', { defaultValue: 'Close' })}
              >
                ×
              </button>
            </header>
            <div className={styles.drawerBody}>
              <pre className={styles.payloadPre}>
                {JSON.stringify(selected.measurements, null, 2)}
              </pre>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
